import { useEffect } from 'react';
import { getCookie } from 'cookies-next';

import { BootstrapSignalKeys, BootstrapSignals } from '@/entities/tracking/lib/bootstrap-signals';
import useTrackingProvider from '@/hooks/providers/useTrackingProvider';
import { useTrackingSiteName } from '@/hooks/tracking/useTrackingSiteName';
import { appLogger } from '@/utils/app-logger/appLogger';
import { isAppDebug } from '@/utils/appDebug';
import { DEVICE_ID_COOKIE, SESSION_ID_COOKIE } from '@/utils/constants/common';
import { DIRECT, PAID_SEARCH, QUERY_PARAM_MAP, REFERRAL_SOURCES } from '@/utils/constants/tracking';
import { isPlaceholderDeviceId } from '@/utils/deviceId';
import { hasAuthCookie } from '@/utils/session/authCookie';
import { getStaticAppInfo, StaticAppInfo } from '@/utils/tracking/getStaticAppInfo';
import { getTrackingBrandPlatform } from '@/utils/tracking/getTrackingBrandPlatform';

import { useTrackDeepLink } from './useTrackDeepLink';

/**
 * Structure to conform to
 * https://docs.google.com/document/d/1DqTn5-sXrTtmSEeqf62pqovzHteyEtyjHeXsgHI1F3A/edit?tab=t.gnni1gga0gec#heading=h.y90tvv565e4p
 */
export interface IAppLaunchContext {
    deepLinkPath?: string;
    deepLinkSource?: string;
    utmSource?: string;
    utmMedium?: string;
    utmCampaign?: string;
    utmContent?: string;
    utmTerm?: string;
    searchTerm?: string;
    ftag?: string;
    kochavaDeviceId?: string;
    kochavaClickId?: string;
    kochavaCampaignId?: string;
}

const parseQueryParams = (search: string): Partial<IAppLaunchContext> => {
    const params = new URLSearchParams(search);
    const result: Partial<IAppLaunchContext> = {};

    for (const [queryKey, contextKey] of Object.entries(QUERY_PARAM_MAP)) {
        const value = params.get(queryKey);
        if (value) {
            result[contextKey as keyof IAppLaunchContext] = value;
        }
    }

    return result;
};

const getReferrerSource = (
    referrer: string,
    queryParams: Partial<IAppLaunchContext>,
): string | null => {
    if (queryParams.kochavaClickId) return PAID_SEARCH;
    if (!referrer) return DIRECT;

    try {
        const { hostname } = new URL(referrer);
        if (hostname === window.location.hostname) return DIRECT;

        const domainParts = hostname.split('.');
        const source = Object.keys(REFERRAL_SOURCES).find(
            (key) => hostname === key || hostname.endsWith(`.${key}`) || domainParts.includes(key),
        );
        return source ? REFERRAL_SOURCES[source] : hostname;
    } catch {
        return null;
    }
};

/**
 * A deeplink is any site load where the landing page is NOT the root (/) or home (/{iso}/home).
 * Returning logged in users are sent to /{iso}/home, but anon users will land on /
 * @param pathname
 * @param referrer
 * @returns The referrer to be logged when the site load is a deeplink
 */
const detectDeeplink = (
    pathname: string,
    referrer: string,
    queryParams: Partial<IAppLaunchContext>,
): string | null => {
    const referrerSource = getReferrerSource(referrer, queryParams);
    // Pluto uses a region aware path, but P+ does not.
    const isHome = pathname.match(/^\/([a-z]{2,5}\/)?home\/?$/);
    return !isHome && pathname !== '/' ? referrerSource : null;
};

let cached: Readonly<IAppLaunchContext> | null = null;
let initialLaunchSnapshot: Readonly<{
    pathname: string;
    search: string;
    referrer: string;
}> | null = null;

const getInitialLaunchSnapshot = () => {
    if (initialLaunchSnapshot || typeof window === 'undefined') {
        return initialLaunchSnapshot;
    }

    initialLaunchSnapshot = Object.freeze({
        pathname: window.location.pathname,
        search: window.location.search,
        referrer: document.referrer,
    });

    return initialLaunchSnapshot;
};

export const resetAppLaunchContext = () => {
    cached = null;
    initialLaunchSnapshot = null;
};

export const getAppLaunchContext = (): Readonly<IAppLaunchContext> => {
    if (cached) return cached;

    const launchSnapshot = getInitialLaunchSnapshot();

    if (!launchSnapshot) {
        return Object.freeze({});
    }

    const { pathname, search, referrer } = launchSnapshot;
    const queryParams = parseQueryParams(search);
    const deepLinkSource = detectDeeplink(pathname, referrer, queryParams);
    const ret: IAppLaunchContext = { ...queryParams };

    if (deepLinkSource) {
        ret.deepLinkPath = pathname + search;
        ret.deepLinkSource = deepLinkSource;
    }
    // spec wants a param called "searchTerm" so map it
    if (queryParams.utmTerm) {
        ret.searchTerm = queryParams.utmTerm;
    }

    cached = Object.freeze(ret);
    return cached;
};

type AppInfoFields = StaticAppInfo & {
    appId?: string;
    appName?: string;
    brandplatformid?: string;
    stitcherSessionId?: string;
    deviceIp?: string;
};
export const useTrackAppLaunch = () => {
    const { trackAppLaunch, trackingData } = useTrackingProvider();
    const { appInfo: data, deviceIp } = trackingData || {};
    const { siteName: resolvedSiteName, isReady: isSiteNameReady } = useTrackingSiteName();
    const siteName = resolvedSiteName ?? '';
    const trackDeepLink = useTrackDeepLink();

    useEffect(() => {
        if (BootstrapSignals.has(BootstrapSignalKeys.APP_INIT)) return;
        // Wait for the live-region siteName before firing the one-shot app launch,
        // so brandplatformid is never emitted from the pinned SSR fallback. Once
        // the regional config settles this effect re-runs and fires with the
        // resolved value.
        if (!isSiteNameReady) return;
        const context = getAppLaunchContext();
        const staticAppInfo = getStaticAppInfo();
        const { brandPlatform } = getTrackingBrandPlatform({ siteName });
        // Read the fpid from the cookie; the client never creates one.
        const fpid = getCookie(DEVICE_ID_COOKIE)?.toString();
        const stitcherSessionId = getCookie(SESSION_ID_COOKIE)?.toString();

        // Log per session if the problem is siteName or fpid. fpid is classified, not logged raw.
        appLogger.info('appLaunch identity snapshot', {
            component: 'useTrackAppLaunch',
            sessionId: stitcherSessionId,
            brandPlatformPresent: Boolean(brandPlatform),
            siteName: siteName || '(empty)',
            isSiteNameReady,
            fpidClass: isPlaceholderDeviceId(fpid) ? 'placeholder' : fpid ? 'valid' : 'absent',
            hasSessionCookie: Boolean(stitcherSessionId),
            hasAuthCookie: hasAuthCookie(),
        });

        const appInfo: AppInfoFields = {
            ...(data?.appId && { appId: data.appId }),
            ...(data?.appName && { appName: data.appName }),
            ...(brandPlatform && { brandplatformid: brandPlatform }),
            ...(stitcherSessionId && { stitcherSessionId }),
            ...(deviceIp && { deviceIp }),
            ...staticAppInfo,
        };
        // Register this FIRST, because appLaunch is going to trigger a flow that fires events in order
        // which will then trigger the deeplink event.
        trackDeepLink(context);
        trackAppLaunch({
            appInfo,
            // No device id: omit identity. Sending fpid: undefined degrades the fpid
            // dataHub already holds and AEP rejects the event (EXEG-0103-400).
            ...(fpid && { identity: { fpid } }),
            context,
            log: {
                isDev: isAppDebug(),
            },
        });
        BootstrapSignals.mark(BootstrapSignalKeys.APP_INIT);
    }, [trackAppLaunch, trackDeepLink, data, siteName, deviceIp, isSiteNameReady]);
};
